/**
 * AdminAreaEditPage — minimal area editor.
 * Lighter than place editor: Berättelse, Visuellt (hero), Struktur (linked places).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, FileText, Image, MapPin, ChevronRight, X, Loader2, CircleDot, Check, Plus, Star, Search, GripVertical } from "lucide-react";
import { AreaShortDescriptionSection } from "@/components/editorial/AreaShortDescriptionSection";
import { AreaNarrativeSection } from "@/components/editorial/AreaNarrativeSection";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { SectionGroupHeading } from "@/components/admin/place-editor/SectionGroupHeading";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAdminArea, useCreateArea, useUpdateArea, useAreaLinkedPlaces, useUnlinkPlaceArea, useLinkPlaceArea, useSetPrimaryArea, useUnsetPrimaryArea, useReorderAreaPlaces, AREA_TYPES } from "@/hooks/useAdminAreas";
import type { AreaLinkedPlace } from "@/hooks/useAdminAreas";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSearchPlacesForLinking } from "@/hooks/useSearchPlacesForLinking";
import { AreaHeroImageSection } from "@/components/editorial/AreaHeroImageSection";
import { useDraftSessionId, clearDraftSessionId } from "@/hooks/useDraftSessionId";
import { finalizeDraftSession } from "@/lib/media/mediaApi";
import { useMediaAssets } from "@/hooks/useMediaAssets";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";

import { cn } from "@/lib/utils";

// ── Schema ──

const areaSchema = z.object({
  name: z.string().min(2, "Namn måste vara minst 2 tecken"),
  slug: z.string().min(2, "Slug måste vara minst 2 tecken"),
  area_type: z.string().min(1, "Välj typ"),
  status: z.enum(["draft", "public", "archived"]),
  description_short: z.string().optional(),
  description_long: z.string().optional(),
  municipality: z.string().optional(),
  county: z.string().optional(),
  landskap: z.string().optional(),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
});

type AreaFormData = z.infer<typeof areaSchema>;

const TYPE_LABELS: Record<string, string> = {
  bygd: "Bygd",
  destination: "Destination",
  landscape_cluster: "Landskapskluster",
  region: "Region",
};

// ── AccordionRow (reused pattern) ──

interface AccordionRowProps {
  icon: React.ReactNode;
  title: string;
  summary: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function AccordionRow({ icon, title, summary, isOpen, onToggle, children }: AccordionRowProps) {
  return (
    <div className="rounded-lg border bg-card text-card-foreground">
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/40",
          isOpen && "border-b"
        )}
      >
        <span className="text-muted-foreground shrink-0">{icon}</span>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-foreground">{title}</span>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{summary}</p>
        </div>
        <ChevronRight
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform duration-200 shrink-0",
            isOpen && "rotate-90"
          )}
        />
      </button>
      {isOpen && (
        <div className="px-4 py-5" onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── Sortable linked place row ──

function SortableLinkedPlaceRow({
  lp,
  areaId,
  setPrimaryMutation,
  unsetPrimaryMutation,
  unlinkMutation,
}: {
  lp: AreaLinkedPlace;
  areaId: string;
  setPrimaryMutation: ReturnType<typeof useSetPrimaryArea>;
  unsetPrimaryMutation: ReturnType<typeof useUnsetPrimaryArea>;
  unlinkMutation: ReturnType<typeof useUnlinkPlaceArea>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lp.place_id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-2 px-3 py-2.5",
        isDragging && "opacity-90 shadow-lg bg-card rounded-lg"
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="shrink-0 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted text-muted-foreground/40 hover:text-muted-foreground transition-colors"
        aria-label="Dra för att ändra ordning"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1">
        <Link
          to={`/app/admin/places/${lp.place_id}/edit`}
          className="text-sm font-medium text-foreground hover:underline truncate block"
        >
          {lp.place_name}
        </Link>
        <div className="flex items-center gap-2 mt-0.5">
          {lp.place_municipality && (
            <span className="text-[11px] text-muted-foreground">
              {lp.place_municipality}
            </span>
          )}
          {lp.is_primary && (
            <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
              Primärt område
            </Badge>
          )}
          {!lp.is_primary && (
            <span className="text-[11px] text-muted-foreground">
              {lp.place_status === "public" ? "Publicerad" : "Utkast"}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {lp.is_primary ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground"
            disabled={unsetPrimaryMutation.isPending}
            onClick={() => unsetPrimaryMutation.mutate({ placeId: lp.place_id, areaId })}
          >
            <Star className="h-3 w-3 mr-1 fill-current" />
            Ta bort primärt
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground"
            disabled={setPrimaryMutation.isPending}
            onClick={() => setPrimaryMutation.mutate({ placeId: lp.place_id, areaId })}
          >
            <Star className="h-3 w-3 mr-1" />
            Sätt primärt
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
          disabled={unlinkMutation.isPending}
          onClick={() => unlinkMutation.mutate({ placeId: lp.place_id, areaId })}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ── Main ──

export default function AdminAreaEditPage() {
  const navigate = useNavigate();
  const { areaId } = useParams<{ areaId: string }>();
  const isNew = !areaId;

  const { data: area, isLoading } = useAdminArea(isNew ? undefined : areaId);
  const createMutation = useCreateArea();
  const updateMutation = useUpdateArea();
  const unlinkMutation = useUnlinkPlaceArea();
  const linkMutation = useLinkPlaceArea();
  const setPrimaryMutation = useSetPrimaryArea();
  const unsetPrimaryMutation = useUnsetPrimaryArea();
  const reorderMutation = useReorderAreaPlaces();
  const { data: linkedPlaces = [] } = useAreaLinkedPlaces(isNew ? undefined : areaId);

  // DnD sensors for linked places reorder
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handlePlaceDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !areaId) return;
    const oldIndex = linkedPlaces.findIndex((lp) => lp.place_id === active.id);
    const newIndex = linkedPlaces.findIndex((lp) => lp.place_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(linkedPlaces, oldIndex, newIndex);
    reorderMutation.mutate({
      areaId,
      orderedPlaceIds: reordered.map((lp) => lp.place_id),
    });
  }, [linkedPlaces, areaId, reorderMutation]);

  // Fetch parent area name for AI context
  const { data: parentArea } = useAdminArea(area?.parent_area_id ?? undefined);

  // Place search for linking
  const [showPlaceSearch, setShowPlaceSearch] = useState(false);
  const [placeSearchQuery, setPlaceSearchQuery] = useState("");
  const { data: searchResults = [], isLoading: searchLoading } = useSearchPlacesForLinking(
    showPlaceSearch ? placeSearchQuery : ""
  );
  const linkedPlaceIds = new Set(linkedPlaces.map((lp) => lp.place_id));

  // Hero media
  const { data: heroAssets = [] } = useMediaAssets("area", areaId || null, "cover", null);

  const draftKey = `area-editor-${areaId ?? "new"}`;
  const draftSessionId = useDraftSessionId(draftKey);

  const [openSection, setOpenSection] = useState<string | null>("story");
  const [justSaved, setJustSaved] = useState(false);
  const justSavedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialRef = useRef<AreaFormData | null>(null);

  const form = useForm<AreaFormData>({
    resolver: zodResolver(areaSchema),
    defaultValues: {
      name: "",
      slug: "",
      area_type: "bygd",
      status: "draft",
      description_short: "",
      description_long: "",
      municipality: "",
      county: "",
      landskap: "",
      lat: null,
      lng: null,
    },
  });

  // Populate form from data
  useEffect(() => {
    if (area) {
      const vals: AreaFormData = {
        name: area.name,
        slug: area.slug,
        area_type: area.area_type,
        status: area.status as any,
        description_short: area.description_short || "",
        description_long: area.description_long || "",
        municipality: area.municipality || "",
        county: area.county || "",
        landskap: area.landskap || "",
        lat: area.lat,
        lng: area.lng,
      };
      form.reset(vals);
      initialRef.current = vals;
    }
  }, [area, form]);

  // Auto-generate slug
  const watchName = form.watch("name");
  useEffect(() => {
    if (isNew && watchName) {
      const slug = watchName
        .toLowerCase()
        .replace(/[åä]/g, "a")
        .replace(/ö/g, "o")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      form.setValue("slug", slug);
    }
  }, [watchName, isNew, form]);

  const currentValues = form.watch();
  const dirty = initialRef.current
    ? JSON.stringify(currentValues) !== JSON.stringify(initialRef.current)
    : isNew
    ? Boolean(currentValues.name)
    : false;

  useEffect(() => {
    if (dirty && justSaved) setJustSaved(false);
  }, [dirty, justSaved]);

  const {
    isConfirmOpen,
    requestNavigate,
    confirmNavigation,
    cancelNavigation,
  } = useUnsavedChangesGuard({ isDirty: dirty });

  const saving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = useCallback(
    async (data: AreaFormData) => {
      const payload = {
        name: data.name,
        slug: data.slug,
        area_type: data.area_type,
        status: data.status,
        description_short: data.description_short || null,
        description_long: data.description_long || null,
        municipality: data.municipality || null,
        county: data.county || null,
        landskap: data.landskap || null,
        lat: data.lat,
        lng: data.lng,
      };

      if (isNew) {
        const created = await createMutation.mutateAsync(payload);
        try {
          await finalizeDraftSession(draftSessionId, "area", created.id);
          clearDraftSessionId(draftKey);
        } catch {}
        navigate(`/app/admin/areas/${created.id}/edit`);
      } else if (areaId) {
        await updateMutation.mutateAsync({ id: areaId, updates: payload });
        initialRef.current = data;
        setJustSaved(true);
        if (justSavedTimer.current) clearTimeout(justSavedTimer.current);
        justSavedTimer.current = setTimeout(() => setJustSaved(false), 3000);
      }
    },
    [isNew, areaId, createMutation, updateMutation, navigate, draftSessionId, draftKey]
  );

  useEffect(() => () => {
    if (justSavedTimer.current) clearTimeout(justSavedTimer.current);
  }, []);

  const goBack = useCallback(() => navigate("/app/admin/areas"), [navigate]);
  const handleBack = () => requestNavigate(goBack);

  const toggleSection = (key: string) =>
    setOpenSection((prev) => (prev === key ? null : key));

  // Summaries
  const v = currentValues;
  const descShortLen = (v.description_short || "").trim().length;
  const descLongLen = (v.description_long || "").trim().length;
  const storySummary = v.name
    ? `${v.name} • ${descShortLen > 0 ? `${descShortLen} tecken` : "kort saknas"}`
    : "Namn saknas";
  const hasCoverAsset = heroAssets.some((a) => a.kind === 'cover');
  const heroSummary =
    heroAssets.length === 0
      ? "Ingen bild uppladdad"
      : hasCoverAsset
        ? `${heroAssets.length} bild${heroAssets.length !== 1 ? "er" : ""} · omslag valt`
        : `${heroAssets.length} bild${heroAssets.length !== 1 ? "er" : ""} · välj omslagsbild`;
  const structureSummary = `${linkedPlaces.length} kopplad${linkedPlaces.length !== 1 ? "e" : ""} plats${linkedPlaces.length !== 1 ? "er" : ""}`;

  if (!isNew && isLoading) {
    return (
      <AdminLayout title="Laddar...">
        <div className="space-y-4 max-w-2xl mx-auto py-12">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      </AdminLayout>
    );
  }

  const isPublished = v.status === "public";

  return (
    <AdminLayout title={isNew ? "Nytt område" : `Redigera ${area?.name || "område"}`}>
      {/* ── Top bar (desktop) ── */}
      <div className="hidden md:block sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="flex h-14 items-center justify-between gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 gap-1.5 text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="h-4 w-4" />
              Tillbaka till områden
            </Button>

            <div className="min-w-0 flex-1 flex items-center justify-center gap-2">
              <p className="truncate text-sm font-medium">
                {isNew ? "Nytt område" : `Redigera: ${v.name || "område"}`}
              </p>
              {!isNew && (
                <Badge
                  variant={isPublished ? "default" : "secondary"}
                  className={
                    isPublished
                      ? "bg-emerald-600 hover:bg-emerald-600 text-white"
                      : ""
                  }
                >
                  {isPublished ? "Publicerad" : "Utkast"}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {dirty && !saving && (
                <span className="flex items-center gap-1.5 text-xs text-destructive font-medium">
                  <CircleDot className="h-3 w-3" />
                  <span className="hidden sm:inline">Ej sparat</span>
                </span>
              )}
              {justSaved && !dirty && (
                <span className="flex items-center gap-1.5 text-xs text-primary font-medium">
                  <Check className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Sparat</span>
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={handleBack}>
                Avbryt
              </Button>
              <Button
                size="sm"
                disabled={(!dirty && !isNew) || saving}
                onClick={() => form.handleSubmit(onSubmit)()}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Sparar…
                  </>
                ) : isNew ? (
                  "Skapa område"
                ) : (
                  "Spara ändringar"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile: back + bottom bar ── */}
      <div className="md:hidden px-4 pt-3 pb-1">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground hover:text-foreground -ml-2"
          onClick={handleBack}
        >
          <ArrowLeft className="h-4 w-4" />
          Tillbaka
        </Button>
      </div>

      <div
        className={cn(
          "md:hidden fixed bottom-[var(--app-bottom-nav-h,0px)] left-0 right-0 z-50",
          "border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80",
          "pb-[env(safe-area-inset-bottom)]"
        )}
      >
        <div className="flex items-center justify-end gap-2 px-4 py-2.5">
          <Button variant="outline" size="sm" onClick={handleBack}>
            Avbryt
          </Button>
          <Button
            size="sm"
            disabled={(!dirty && !isNew) || saving}
            onClick={() => form.handleSubmit(onSubmit)()}
          >
            {saving ? "Sparar…" : isNew ? "Skapa" : "Spara"}
          </Button>
        </div>
      </div>

      <div className="h-3" />

      {/* ── Form ── */}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="pb-16 md:pb-16 max-w-4xl mx-auto"
          style={{ paddingBottom: "max(4rem, calc(60px + env(safe-area-inset-bottom)))" }}
        >
          <div className="space-y-2">
            {/* ── Berättelse ── */}
            <SectionGroupHeading title="Områdets identitet" />

            <AccordionRow
              icon={<FileText className="h-4 w-4" />}
              title="Berättelse"
              summary={storySummary}
              isOpen={openSection === "story"}
              onToggle={() => toggleSection("story")}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Namn *</FormLabel>
                        <FormControl>
                          <Input placeholder="t.ex. Siljansbygden" {...field} className="h-9" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="slug"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Slug *</FormLabel>
                        <FormControl>
                          <Input placeholder="siljansbygden" {...field} className="h-9" />
                        </FormControl>
                        <FormDescription className="text-[11px] text-muted-foreground/70">
                          Används i URL:en
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="area_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Typ *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Välj typ" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-popover">
                            {AREA_TYPES.map((t) => (
                              <SelectItem key={t} value={t}>
                                {TYPE_LABELS[t] || t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Status</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-popover">
                            <SelectItem value="draft">Utkast</SelectItem>
                            <SelectItem value="public">Publicerad</SelectItem>
                            <SelectItem value="archived">Arkiverad</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <AreaShortDescriptionSection
                  form={form}
                  areaName={v.name}
                  parentAreaName={parentArea?.name}
                  areaType={v.area_type}
                  landskap={v.landskap}
                  municipality={v.municipality}
                  linkedPlaces={linkedPlaces}
                  disabled={!v.name}
                />

                <AreaNarrativeSection
                  form={form}
                  areaName={v.name}
                  parentAreaName={parentArea?.name}
                  areaType={v.area_type}
                  landskap={v.landskap}
                  municipality={v.municipality}
                  descriptionShort={v.description_short}
                  linkedPlaces={linkedPlaces}
                  disabled={!v.name}
                />
              </div>
            </AccordionRow>

            {/* ── Visuellt ── */}
            <SectionGroupHeading title="Visuellt" />

            <AccordionRow
              icon={<Image className="h-4 w-4" />}
              title="Hero-bild"
              summary={heroSummary}
              isOpen={openSection === "visual"}
              onToggle={() => toggleSection("visual")}
            >
              <AreaHeroImageSection
                areaId={areaId}
                requiresSave={!areaId}
              />
            </AccordionRow>

            {/* ── Struktur ── */}
            {!isNew && areaId && (
              <>
                <SectionGroupHeading
                  title="Struktur"
                  subtitle="Platser kopplade till detta område."
                />

                <AccordionRow
                  icon={<MapPin className="h-4 w-4" />}
                  title="Kopplade platser"
                  summary={structureSummary}
                  isOpen={openSection === "structure"}
                  onToggle={() => toggleSection("structure")}
                >
                  <div className="space-y-3">
                    {/* Add place search */}
                    {!showPlaceSearch ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => setShowPlaceSearch(true)}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Lägg till plats
                      </Button>
                    ) : (
                      <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                            <Input
                              autoFocus
                              placeholder="Sök plats…"
                              value={placeSearchQuery}
                              onChange={(e) => setPlaceSearchQuery(e.target.value)}
                              className="h-8 pl-8 text-sm"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-muted-foreground"
                            onClick={() => {
                              setShowPlaceSearch(false);
                              setPlaceSearchQuery("");
                            }}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="max-h-48 overflow-y-auto divide-y rounded border bg-background">
                          {searchLoading ? (
                            <div className="px-3 py-4 text-center">
                              <Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" />
                            </div>
                          ) : searchResults.filter((p) => !linkedPlaceIds.has(p.id)).length === 0 ? (
                            <p className="text-xs text-muted-foreground px-3 py-3">
                              {placeSearchQuery ? "Inga matchande platser hittades." : "Skriv för att söka bland platser."}
                            </p>
                          ) : (
                            searchResults
                              .filter((p) => !linkedPlaceIds.has(p.id))
                              .map((place) => (
                                <button
                                  key={place.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors flex items-center justify-between gap-2"
                                  disabled={linkMutation.isPending}
                                  onClick={() => {
                                    linkMutation.mutate(
                                      { placeId: place.id, areaId: areaId! },
                                      {
                                        onSuccess: () => {
                                          setPlaceSearchQuery("");
                                          setShowPlaceSearch(false);
                                        },
                                      }
                                    );
                                  }}
                                >
                                  <div className="min-w-0">
                                    <span className="text-sm font-medium text-foreground block truncate">
                                      {place.name}
                                    </span>
                                    {place.municipality && (
                                      <span className="text-[11px] text-muted-foreground">
                                        {place.municipality}
                                      </span>
                                    )}
                                  </div>
                                  <Plus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                </button>
                              ))
                          )}
                        </div>
                      </div>
                    )}

                    {/* Linked places list */}
                    {linkedPlaces.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2">
                        Inga platser är kopplade till detta område ännu.
                      </p>
                    ) : (
                      <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handlePlaceDragEnd}>
                        <SortableContext items={linkedPlaces.map((lp) => lp.place_id)} strategy={verticalListSortingStrategy}>
                          <div className="divide-y rounded-lg border">
                            {linkedPlaces.map((lp) => (
                              <SortableLinkedPlaceRow
                                key={lp.place_id}
                                lp={lp}
                                areaId={areaId!}
                                setPrimaryMutation={setPrimaryMutation}
                                unsetPrimaryMutation={unsetPrimaryMutation}
                                unlinkMutation={unlinkMutation}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                </AccordionRow>
              </>
            )}
          </div>
        </form>
      </Form>

      {/* Unsaved changes dialog */}
      <AlertDialog open={isConfirmOpen} onOpenChange={(open) => !open && cancelNavigation()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Osparade ändringar</AlertDialogTitle>
            <AlertDialogDescription>
              Du har osparade ändringar. Vill du lämna utan att spara?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelNavigation}>Stanna kvar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmNavigation}>Lämna ändå</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
